import type { DragEvent } from "react"
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react"

import type { ReportTemplateFilterDto } from "../../api/reportTemplateService"
import { Button } from "shared/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"

export interface FiltersTabProps {
  filters: ReportTemplateFilterDto[]
  dragFilterIndex: number | null
  dragOverFilterIndex: number | null
  onAddFilter: () => void
  onEditFilter: (index: number) => void
  onDeleteFilter: (index: number) => void
  onFilterDragStart: (index: number) => void
  onFilterDragOver: (index: number, event: DragEvent<HTMLTableRowElement>) => void
  onFilterDrop: (index: number) => void
  onFilterDragEnd: () => void
}

export function FiltersTab({
  filters,
  dragFilterIndex,
  dragOverFilterIndex,
  onAddFilter,
  onEditFilter,
  onDeleteFilter,
  onFilterDragStart,
  onFilterDragOver,
  onFilterDrop,
  onFilterDragEnd,
}: FiltersTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">Drag and drop each row to reorder filters.</p>
        <Button onClick={onAddFilter} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Filter
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[52px]"></TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Field Key</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Default Value JSON</TableHead>
              <TableHead>Required</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-gray-500">
                  No filters configured
                </TableCell>
              </TableRow>
            ) : (
              filters.map((item, index) => (
                <TableRow
                  key={`${item.fieldKey}-${index}`}
                  draggable
                  onDragStart={() => onFilterDragStart(index)}
                  onDragOver={(event) => onFilterDragOver(index, event)}
                  onDrop={() => onFilterDrop(index)}
                  onDragEnd={onFilterDragEnd}
                  className={[
                    "cursor-move",
                    dragFilterIndex === index ? "opacity-60" : "",
                    dragOverFilterIndex === index && dragFilterIndex !== index ? "bg-blue-50/70" : "",
                  ]
                    .join(" ")
                    .trim()}
                >
                  <TableCell>
                    <div className="flex items-center justify-center text-gray-400">
                      <GripVertical className="h-4 w-4" />
                    </div>
                  </TableCell>
                  <TableCell>{item.orderIndex}</TableCell>
                  <TableCell className="font-medium">{item.fieldKey}</TableCell>
                  <TableCell>{item.operator}</TableCell>
                  <TableCell className="max-w-[360px] truncate">{item.defaultValueJson || "-"}</TableCell>
                  <TableCell>{item.required ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => onEditFilter(index)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onDeleteFilter(index)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
